import { useMemo } from 'react';
import { IBookmark } from '../common/types/IBookmark';
import { predefinedCategories, categoriesIcon, categoryColors } from '../common/constants/categories';

export function useCategories(bookmarks: IBookmark[]) {
  // Get all categories that have bookmarks
  const activeCategories = useMemo(() => {
    const uniqueCategories = new Set<string>();
    bookmarks.forEach(bookmark => {
      bookmark.categories?.forEach(category => {
        uniqueCategories.add(category);
      });
    });
    return uniqueCategories;
  }, [bookmarks]);

  // Get count of bookmarks for each category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    bookmarks.forEach(bookmark => {
      bookmark.categories?.forEach(category => {
        counts[category] = (counts[category] || 0) + 1;
      });
    });
    return counts;
  }, [bookmarks]);

  // Filter and sort predefined categories
  const categories = useMemo(() => {
    return predefinedCategories
      .map(category => ({
        id: category,
        name: category,
        icon: categoriesIcon[category],
        color: categoryColors[category],
        count: categoryCounts[category] || 0,
        isActive: activeCategories.has(category),
      }))
      .sort((a, b) => {
        // Sort by active status first
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        // Then by count
        if (a.count !== b.count) return b.count - a.count;
        // Then alphabetically
        return a.name.localeCompare(b.name);
      });
  }, [activeCategories, categoryCounts]);

  return {
    categories,
    getCategoryCount: (category: string) => categoryCounts[category] || 0,
    getCategoryIcon: (category: string) => categoriesIcon[category],
    getCategoryColor: (category: string) => categoryColors[category],
    isActiveCategory: (category: string) => activeCategories.has(category),
  };
}
